import {
  Tutorial as ApiTutorial,
  TutorialFilter,
  fetchPinnedTutorials,
  fetchPlaylist,
  fetchTutorials,
} from "@/lib/api";
import { filterTutorialsByCompletionStatus } from "@/lib/filterUtils";
import {
  getCompletedTutorialIds,
  markTutorialAsCompleted,
  unmarkTutorialAsCompleted,
} from "@/lib/supabase";
import {
  FilterReason,
  FilterType,
  Tutorial,
  TutorialFilters,
} from "@/types/tutorial";
import { useUser } from "@supabase/auth-helpers-react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useUninterestedTutorials } from "./useUninterestedTutorials";

/**
 * Simplified hook for fetching and filtering tutorials with pagination support
 */
// Rename Tags on Playlist ids
export function useTutorials({
  selectedTags = [],
  excludedTags = [],
  pageSize = 15,
}: Partial<TutorialFilters> & { pageSize?: number }) {
  // State to track when we're auto-loading more pages due to filtering
  const [isAutoLoadingMorePages, setIsAutoLoadingMorePages] = useState(false);

  // Get user statuses (completed/uninterested)
  const {
    completedIds,
    uninterestedIds,
    isLoading: isStatusLoading,
    hasError: hasStatusError,
    error: statusError,
  } = useUserTutorialStatuses();

  // Create filters for API request
  const documentFilters = useFilters({
    completedIds,
    uninterestedIds,
    selectedTags,
  });

  // Remove special tags that should not be sent to the server
  const filteredTags = filterSpecialTags(selectedTags);
  const filteredSelectedTags = filteredTags.length > 0 ? filteredTags : ["all"];

  type TutorialsResponse = {
    tutorials: ApiTutorial[];
    pagination?: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
    tag?: { name: string };
  };

  // Load tutorials with infinite query for pagination
  const tutorialsQuery = useInfiniteQuery<TutorialsResponse>({
    queryKey: tutorialKeys.infiniteFilters({
      selectedTags: filteredSelectedTags,
      documentFilters,
    }),
    queryFn: async ({ pageParam }) => {
      try {
        const isSpecialTag = selectedTags.some((tag) =>
          ["not interested", "completed"].includes(tag)
        );

        let tutorialsRes;
        let pinnedTutorials = [];

        if (!isSpecialTag && !filteredSelectedTags.includes("all")) {
          // For feed-based requests, we don't use pagination currently
          if (pageParam === 1) {
            tutorialsRes = await fetchPlaylist({
              documentId: filteredSelectedTags[0],
            });

            return {
              ...(tutorialsRes.tag?.name && { tag: tutorialsRes.tag.name }),
              tutorials: tutorialsRes.tutorials.sort((a, b) => {
                if (a.is_pinned && !b.is_pinned) return -1;
                if (!a.is_pinned && b.is_pinned) return 1;
                return 0;
              }),
              pagination: {
                page: 1,
                pageSize: tutorialsRes.tutorials.length,
                pageCount: 1,
                total: tutorialsRes.tutorials.length,
              },
            };
          } else {
            // No more pages for feeds
            return {
              tutorials: [],
              pagination: {
                page: pageParam as number,
                pageSize,
                pageCount: 1,
                total: 0,
              },
            };
          }
        }

        // Fetch pinned tutorials only on first page
        if (pageParam === 1) {
          pinnedTutorials = await fetchPinnedTutorials({
            documentFilters,
          });
        }

        tutorialsRes = await fetchTutorials({
          documentFilters,
          page: pageParam as number,
          pageSize,
        });
        const allTutorials = [...pinnedTutorials, ...tutorialsRes.tutorials];
        const uniqueTutorials = allTutorials.filter(
          (tutorial, index, arr) =>
            arr.findIndex((t) => t.documentId === tutorial.documentId) === index
        );

        return {
          tutorials: uniqueTutorials,
          pagination: tutorialsRes.pagination,
        };
      } catch (error) {
        console.error("Error fetching tutorials:", error);
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pagination &&
        lastPage.pagination.page < lastPage.pagination.pageCount
        ? lastPage.pagination.page + 1
        : undefined;
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
    retry: 2,
  });
  // Extract all tutorials from all pages
  const tutorials = useMemo(() => {
    if (!tutorialsQuery.data?.pages) return [];

    return tutorialsQuery.data.pages.flatMap((page) => {
      if (!page || !Array.isArray(page.tutorials)) return [];
      return page.tutorials.map(convertToAppTutorial);
    });
  }, [tutorialsQuery.data?.pages]);

  // Track if client-side filters are being applied
  const isClientSideFiltering = useMemo(
    () =>
      (excludedTags && excludedTags.length > 0) ||
      selectedTags.includes("completed") ||
      selectedTags.includes("not interested"),
    [excludedTags, selectedTags]
  );

  // Apply client-side filters
  const filteredTutorials = useMemo((): Tutorial[] => {
    if (!tutorials.length) return [];

    try {
      // 1. Filter by excluded tags and tools
      const excludedFiltered = filterTutorialsByExclusions(
        tutorials,
        excludedTags
      );

      // 2. Filter by status (completed/not interested)
      const statusFiltered = filterTutorialsByCompletionStatus(
        excludedFiltered,
        {
          completedStatus: selectedTags.includes("completed")
            ? "selected"
            : undefined,
          notInterestedStatus: selectedTags.includes("not interested")
            ? "selected"
            : undefined,
        },
        completedIds,
        uninterestedIds
      );

      return statusFiltered;
    } catch (error) {
      console.error("Error filtering tutorials:", error);
      return [];
    }
  }, [tutorials, excludedTags, completedIds, uninterestedIds, selectedTags]);

  // Create convenience function for pagination
  const fetchMore = useCallback(() => {
    if (tutorialsQuery.hasNextPage && !tutorialsQuery.isFetchingNextPage) {
      tutorialsQuery.fetchNextPage();
    }
  }, [tutorialsQuery]);

  // Effect to handle auto-loading more pages when all tutorials are filtered out
  useEffect(() => {
    // Only run this effect when we have tutorials and exclusion filters
    if (!tutorials.length || !excludedTags?.length) {
      return;
    }

    // Check if all tutorials are filtered out after applying exclusions
    const allFiltered =
      filterTutorialsByExclusions(tutorials, excludedTags).length === 0;

    if (
      allFiltered &&
      tutorialsQuery.hasNextPage &&
      !tutorialsQuery.isFetchingNextPage &&
      !isAutoLoadingMorePages
    ) {
      console.log(
        "All tutorials were filtered out, loading next page automatically"
      );
      setIsAutoLoadingMorePages(true);
      tutorialsQuery.fetchNextPage().then(() => {
        setIsAutoLoadingMorePages(false);
      });
    } else if (!allFiltered && isAutoLoadingMorePages) {
      // Reset loading state when we have results
      setIsAutoLoadingMorePages(false);
    }
  }, [tutorials, excludedTags, tutorialsQuery, isAutoLoadingMorePages]);

  // Loading and error state
  const isLoading =
    tutorialsQuery.isLoading ||
    isStatusLoading ||
    (isClientSideFiltering && tutorialsQuery.isRefetching) ||
    isAutoLoadingMorePages;
  const hasErrors = !!tutorialsQuery.error || hasStatusError;
  const error = tutorialsQuery.error || statusError;

  // Query client for cache operations
  const queryClient = useQueryClient();
  const user = useUser();
  const userId = user?.id;

  // Update tutorial completion status
  const updateCompletionStatus = useCallback(
    async (tutorialId: string, isCompleted: boolean) => {
      if (!userId) return;

      try {
        // Update database
        if (isCompleted) {
          await markTutorialAsCompleted(userId, tutorialId);
        } else {
          await unmarkTutorialAsCompleted(userId, tutorialId);
        }

        // Update local completed IDs array
        const newCompletedIds = isCompleted
          ? [...completedIds, tutorialId]
          : completedIds.filter((id) => id !== tutorialId);

        // Update cache
        queryClient.setQueryData(
          tutorialKeys.completed(userId),
          newCompletedIds
        );

        // Invalidate tutorial queries for refresh
        queryClient.invalidateQueries({ queryKey: tutorialKeys.all });
        queryClient.invalidateQueries({
          queryKey: ["tutorial-preferences", userId],
        });
        queryClient.invalidateQueries({ queryKey: ["all-tag-tutorials"] });
        queryClient.invalidateQueries({ queryKey: ["tags"] });
      } catch (error) {
        console.error("Error updating tutorial completion status:", error);
        throw error;
      }
    },
    [userId, completedIds, queryClient]
  );

  return {
    // Data
    tutorials: filteredTutorials,
    completedIds,
    uninterestedIds,
    alltutorialsByFeed: tutorials,
    tagName: tutorialsQuery.data?.pages?.[0]?.tag,

    // Pagination helpers
    fetchNextPage: tutorialsQuery.fetchNextPage,
    fetchMore,
    hasNextPage: tutorialsQuery.hasNextPage,
    isFetchingNextPage: tutorialsQuery.isFetchingNextPage,

    // State
    isLoading,
    isRefetching: tutorialsQuery.isRefetching,
    hasErrors,
    error,

    // Actions
    updateCompletionStatus,
    refetch: tutorialsQuery.refetch, // Expose manual refetch
  };
}

/**
 * Filter tutorials by excluded tags and tools
 */
function filterTutorialsByExclusions(
  tutorials: Tutorial[],
  excludedTags?: string[],
  excludedTools?: string[]
): Tutorial[] {
  // If there are no exclusion filters, return all tutorials
  if (
    (!excludedTags || excludedTags.length === 0) &&
    (!excludedTools || excludedTools.length === 0)
  ) {
    return tutorials;
  }

  const lowerExcludedTags = excludedTags?.map((tag) => tag.toLowerCase()) || [];
  const lowerExcludedTools =
    excludedTools?.map((tool) => tool.toLowerCase()) || [];

  return tutorials.filter((tutorial) => {
    // Check excluded tags
    if (lowerExcludedTags.length > 0) {
      const hasExcludedTag = tutorial.tags.some((tag) =>
        lowerExcludedTags.includes(tag.toLowerCase())
      );
      if (hasExcludedTag) return false;
    }

    // Check excluded tools
    if (lowerExcludedTools.length > 0) {
      const hasExcludedTool = tutorial.tools.some((tool) =>
        lowerExcludedTools.includes(tool.name.toLowerCase())
      );
      if (hasExcludedTool) return false;
    }

    return true;
  });
}

/**
 * Convert API Tutorial to app format
 */
function convertToAppTutorial(apiTutorial: ApiTutorial): Tutorial {
  return {
    id: apiTutorial.id,
    documentId: apiTutorial.documentId,
    title: apiTutorial.title,
    description: apiTutorial.description,
    tags: apiTutorial.tags || [],
    tools: apiTutorial.tools || [],
    thumbnailUrl: apiTutorial.thumbnailUrl,
    creator: apiTutorial.creator,
    updatedAt:
      apiTutorial.updatedAt instanceof Date
        ? apiTutorial.updatedAt
        : new Date(apiTutorial.updatedAt),
    duration: apiTutorial.duration,
    url: `/${apiTutorial.slug || ""}`,
    is_pinned: apiTutorial.is_pinned,
    is_trial: apiTutorial.is_trial || false,
    order_index: apiTutorial.order_index,
  };
}

export const tutorialKeys = {
  all: ["tutorials"] as const,
  lists: () => [...tutorialKeys.all, "list"] as const,
  infinite: () => [...tutorialKeys.lists(), "infinite"] as const,
  filters: (filters: {
    selectedTags: string[];
    selectedTools: string[];
    documentFilters: TutorialFilter[];
  }) => [...tutorialKeys.lists(), filters] as const,
  infiniteFilters: (filters: {
    selectedTags: string[];
    documentFilters: TutorialFilter[];
  }) => [...tutorialKeys.infinite(), filters] as const,
  user: (userId?: string) => [...tutorialKeys.all, "user", userId] as const,
  completed: (userId?: string) =>
    [...tutorialKeys.user(userId), "completed"] as const,
  uninterested: (userId?: string) =>
    [...tutorialKeys.user(userId), "uninterested"] as const,
  preferences: (userId?: string, documentIds?: string[]) =>
    [...tutorialKeys.user(userId), "preferences", documentIds] as const,
};

/**
 * Hook for getting user tutorial statuses
 */
function useUserTutorialStatuses() {
  const user = useUser();

  // Get completed tutorials
  const completedQuery = useQuery({
    queryKey: tutorialKeys.completed(user?.id),
    queryFn: async () => {
      if (!user?.id) return [];
      try {
        return await getCompletedTutorialIds(user.id);
      } catch (error) {
        console.error("Error fetching completed tutorials:", error);
        return [];
      }
    },
    enabled: !!user?.id,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  // Use existing hook for uninteresting tutorials
  const {
    uninterestedIds,
    isLoading: isUninterestedLoading,
    isError: hasUninterestedError,
    error: uninterestedError,
  } = useUninterestedTutorials(user?.id);

  return {
    completedIds: completedQuery.data || [],
    uninterestedIds:
      uninterestedIds.filter((el) => !completedQuery.data?.includes(el)) || [],
    isLoading: completedQuery.isLoading || isUninterestedLoading,
    hasError: !!completedQuery.error || hasUninterestedError,
    error: completedQuery.error || uninterestedError,
  };
}

/**
 * Create filters for server requests
 */
function useFilters({
  completedIds,
  uninterestedIds,
  selectedTags,
}: {
  completedIds: string[];
  uninterestedIds: string[];
  selectedTags: string[];
}) {
  return useMemo(() => {
    const filters = [];
    const safeCompletedIds = completedIds || [];
    const safeUninterestedIds = uninterestedIds || [];
    const safeSelectedTags = selectedTags || [];

    // Processing completed tutorials
    if (safeCompletedIds.length) {
      if (safeSelectedTags.includes("completed")) {
        // Include ONLY completed tutorials
        filters.push({
          documentIds: safeCompletedIds,
          type: FilterType.Include,
          reason: FilterReason.Completed,
        });
      } else {
        // Default: exclude completed tutorials
        filters.push({
          documentIds: safeCompletedIds,
          type: FilterType.Exclude,
          reason: FilterReason.Completed,
        });
      }
    }

    // Processing uninteresting tutorials
    if (safeUninterestedIds.length) {
      if (safeSelectedTags.includes("not interested")) {
        // Include ONLY uninteresting tutorials
        filters.push({
          documentIds: safeUninterestedIds,
          type: FilterType.Include,
          reason: FilterReason.Uninterested,
        });
      } else {
        // Default: exclude uninteresting tutorials
        filters.push({
          documentIds: safeUninterestedIds,
          type: FilterType.Exclude,
          reason: FilterReason.Uninterested,
        });
      }
    }

    return filters;
  }, [completedIds, uninterestedIds, selectedTags]);
}

/**
 * Filter special tags
 */
function filterSpecialTags(selectedTags: string[]) {
  if (!selectedTags || !Array.isArray(selectedTags)) return [];
  return selectedTags.filter(
    (tag) => tag !== "completed" && tag !== "not interested"
  );
}
